import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { createApi } from '../utils/api';

const ApiExample = () => {
  const { isAuthenticated, user, getAccessTokenSilently } = useAuth0();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newTodo, setNewTodo] = useState('');

  // Create API instance with Auth0 token function, memoized to prevent infinite loops
  const api = useMemo(() => createApi(getAccessTokenSilently), [getAccessTokenSilently]);

  // Memoize fetchTodos to prevent infinite re-renders
  const fetchTodos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getTodos();
      setTodos(data);
      console.log('Fetched todos:', JSON.stringify(data));
    } catch (err) {
      setError('Failed to fetch todos. Backend might not be running yet.');
      console.error('Error fetching todos:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Fetch todos when component mounts
  useEffect(() => {
    if (isAuthenticated) {
      fetchTodos();
    }
  }, [isAuthenticated, fetchTodos]);

  const handleCreateTodo = async (e) => {
    e.preventDefault();
    if (!newTodo.trim()) return;

    setLoading(true);
    try {
      const todo = await api.createTodo({
        title: newTodo,
        completed: false
      });
      setTodos([...todos, todo]);
      setNewTodo('');
    } catch (err) {
      setError('Failed to create todo');
      console.error('Error creating todo:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTodo = async (id) => {
    setLoading(true);
    try {
      await api.deleteTodo(id);
      setTodos(todos.filter(todo => todo.id !== id));
    } catch (err) {
      setError('Failed to delete todo');
      console.error('Error deleting todo:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="text-center p-4">
        <p>Please log in to see the API example.</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto p-4">
      <h2 className="text-xl font-bold mb-4">API Example</h2>
      <p className="text-sm text-gray-600 mb-4">
        Welcome, {user.name}! This demonstrates authenticated API calls.
      </p>

      {/* Create Todo Form */}
      <form onSubmit={handleCreateTodo} className="mb-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={newTodo}
            onChange={(e) => setNewTodo(e.target.value)}
            placeholder="Add a new todo..."
            className="flex-1 px-3 py-2 border rounded"
            disabled={loading}
          />
          <button
            type="submit"
            disabled={loading || !newTodo.trim()}
            className="px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50"
          >
            Add
          </button>
        </div>
      </form>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Todos List */}
      <div className="space-y-2">
        {loading ? (
          <p className="text-center text-gray-500">Loading...</p>
        ) : todos.length === 0 ? (
          <p className="text-center text-gray-500">
            No todos yet. Add one above!
          </p>
        ) : (
          todos.map((todo) => (
            <div
              key={todo.id}
              className="flex items-center justify-between p-3 bg-gray-50 rounded"
            >
              <span className={todo.completed ? 'line-through' : ''}>
                {todo.title}
              </span>
              <button
                onClick={() => handleDeleteTodo(todo.id)}
                className="text-red-500 hover:text-red-700"
                disabled={loading}
              >
                Delete
              </button>
            </div>
          ))
        )}
      </div>

      {/* Backend Status */}
      <div className="mt-4 p-3 bg-gray-100 rounded">
        <p className="text-sm text-gray-600">
          <strong>Backend Status:</strong> {error ? 'Not connected' : 'Connected'}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Set REACT_APP_API_URL in your environment variables to point to your backend.
        </p>
      </div>
    </div>
  );
};

export default ApiExample; 